import { useQuery } from "@apollo/client";
import { AppliedFilters, Table } from "@fluidattacks/design";
import isEmpty from "lodash/isEmpty";
import { StrictMode, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { usePriorityFilters } from "./filters";
import { useAdditionalInfo } from "./hooks";
import { GET_VULNS_PRIORITY_RANKING } from "./queries";
import type {
  IPriorityRanking,
  IPriorityVulnerabilities,
  IVulnerabilityAttr,
} from "./types";

import type { IVulnRowAttr } from "features/vulnerabilities/types";
import { formatVulnerabilitiesTreatment } from "features/vulnerabilities/utils";
import { VulnerabilityModal } from "features/vulnerabilities/vulnerability-modal";
import { useAudit } from "hooks/use-audit";
import { useTable } from "hooks/use-table";
import { Logger } from "utils/logger";

const PriorityRanking = ({ columns }: IPriorityRanking): JSX.Element => {
  const { addAuditEvent } = useAudit();
  const [treatment, setTreatment] = useState("");
  const [search, setSearch] = useState("");
  const { vulnerabilityId: vulnId } = useParams() as {
    vulnerabilityId?: string;
  };
  const [currentRow, setCurrentRow, openAdditionalInfoModal] =
    useAdditionalInfo();

  const { data, loading } = useQuery<IPriorityVulnerabilities>(
    GET_VULNS_PRIORITY_RANKING,
    {
      fetchPolicy: "network-only",
      onCompleted: (): void => {
        addAuditEvent("ToDo.Priority", "unknown");
      },
      onError: ({ graphQLErrors }): void => {
        graphQLErrors.forEach((error): void => {
          Logger.error(
            "An error occurred loading vulnerabilities priority top 10",
            error,
          );
        });
      },
      variables: { treatment: isEmpty(treatment) ? null : treatment },
    },
  );

  const { edges } =
    data === undefined ? { edges: [] } : data.me.vulnerabilitiesPriorityRanking;
  const unformattedVulns = useMemo((): IVulnerabilityAttr[] => {
    return [
      ...edges.map((edge: { node: IVulnerabilityAttr }): IVulnerabilityAttr => {
        return { ...edge.node };
      }),
    ];
  }, [edges]);
  const vulnerabilities = useMemo((): IVulnRowAttr[] => {
    return formatVulnerabilitiesTreatment({
      organizationsGroups: undefined,
      vulnerabilities: unformattedVulns,
    });
  }, [unformattedVulns]);

  const formattedData = useMemo(
    (): IVulnerabilityAttr[] =>
      unformattedVulns.filter(({ where }): boolean =>
        where.toLowerCase().includes(search.toLowerCase()),
      ),
    [unformattedVulns, search],
  );

  const { table } = useTable({
    columns,
    data: formattedData,
    id: "fix-priority-table",
    options: {
      actionsOptions: { onSearch: setSearch, search },
      filtersMode: "client-side",
      loading,
      paginationMode: "client-side",
      rowOptions: { onClick: openAdditionalInfoModal },
    },
    total: formattedData.length,
  });

  const { Filters, options, removeFilter } = usePriorityFilters({
    setTreatment,
  });

  useEffect((): void => {
    if (vulnId !== undefined) {
      setCurrentRow(
        vulnerabilities.find((vuln): boolean => vuln.id === vulnId),
      );
    }
  }, [setCurrentRow, vulnerabilities, vulnId]);

  return (
    <StrictMode>
      <Table table={table}>
        <Table.Options>
          <Table.Controls>
            <Filters />
          </Table.Controls>
        </Table.Options>
        <Table.Caption>
          <AppliedFilters onClose={removeFilter} options={options} />
        </Table.Caption>
        <Table.Content />
        <Table.Footer />
      </Table>
      {currentRow && vulnId !== undefined ? (
        <VulnerabilityModal
          cvssVersion={"severityThreatScore"}
          findingId={currentRow.findingId}
          groupName={currentRow.groupName}
          vulnerabilityId={vulnId}
        />
      ) : undefined}
    </StrictMode>
  );
};

export { PriorityRanking };
